import Image from "next/image";
import { useTranslation } from "next-i18next";
import { PageQuery, Get_SubscriptionQuery } from "gql/graphql";

import Button from "./Button/Button";
import ButtonOrLink from "components/Button/ButtonOrLink";
import ChevronRight from "public/assets/chevron-right.svg";
import Explicit from "public/assets/explicit.svg";
import Listen from "components/Listen";
import Lock from "public/assets/lock.svg";
import Stripe from "public/assets/stripe.svg";
import LinkSlash from "/public/assets/link-slash.svg";
import Toast from "./Toast/Toast";
import { useState } from "react";
import { ArrowPathIcon } from "@heroicons/react/20/solid";

import SubscriptionDetailTable from "./SubscriptionDetailTable/SubscriptionDetailTable";

export default function SubscriptionDetails({
  subscriptionData,
  data,
  multipleShows,
  displayShowDetailsDialog,
  token,
}: {
  subscriptionData: Get_SubscriptionQuery | undefined;
  data: PageQuery;
  multipleShows: boolean;
  displayShowDetailsDialog: (show: any) => void;
  token: string;
}) {
  const { t } = useTranslation("common");
  const [showSpotifyUnlinkedToast, setShowSpotifyUnlinkedToast] = useState(false);
  const [isSpotifyUnlinked, setIsSpotifyUnlinked] = useState(false);
  const [isUnlinkingSpotify, setIsUnlinkingSpotify] = useState(false);
  const unlinkSpotify = async () => {
    setIsUnlinkingSpotify(true);

    const response = await fetch("/api/unlink-spotify", {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ authToken: token }),
    }).then((res) => res.json());
    if (response) {
      setIsUnlinkingSpotify(false);
      setShowSpotifyUnlinkedToast(true);
      setIsSpotifyUnlinked(true);
    }
  };

  return (
    <>
      <Toast show={showSpotifyUnlinkedToast} onClose={() => setShowSpotifyUnlinkedToast(false)}>
        Spotify successfully unlinked
      </Toast>
      <h2 className="font-display text-[32px] font-bold leading-none lg:text-[40px]">
        {t("subscribed_header")}
      </h2>

      <p className="mt-6 text-white/70">
        {t("subscribed_subheader1")}{" "}
        <span className="text-white/90">{subscriptionData?.subscription?.product.name}</span>
      </p>
      <p className="mt-2 text-white/70">{t("subscribed_subheader2")}</p>

      {!multipleShows && (
        <div className="mt-8">
          <Listen
            playbackOptions={subscriptionData!.subscription?.product.shows[0].playbackOptions}
          />
        </div>
      )}
      {multipleShows && (
        <section className="mt-11">
          <h2 className="flex items-center gap-4 text-sm font-semibold text-white lg:text-base">
            <span>{t("shows")}</span>
            <span className="h-px w-full flex-1 bg-white/[.08]"></span>
          </h2>
          <ul className="mt-6 space-y-5">
            {subscriptionData?.subscription?.product.shows.map((show, i) => (
              <li key={i}>
                <ButtonOrLink
                  onClick={() =>
                    displayShowDetailsDialog({
                      title: show.title,
                      productTitle: data?.page?.hero.title,
                      coverImageUrl: show.coverImageUrl,
                      details: data?.page?.hero.metadata.join("・"),
                      premium: true,
                      explicit: show.explicit,
                      playbackOptions: show.playbackOptions,
                    })
                  }
                  className="group flex w-full items-center gap-4 rounded focus:shadow-input-focus focus:outline-none focus:ring-0"
                >
                  <span className="h-14 w-14 shrink-0">
                    <Image
                      className="w-l4 h-14 rounded"
                      src={show.coverImageUrl || ""}
                      width="56"
                      height="56"
                      quality={100}
                      alt={"Cover image for " + show.title}
                    />
                  </span>
                  <div className="flex-1 overflow-hidden">
                    <div className="flex items-center justify-between gap-4">
                      <span className="font-semibold">{show.title}</span>
                      <ChevronRight className="h-6 w-6 fill-white/50 transition group-hover:fill-white/90" />
                    </div>
                    <p className="mt-0.5 flex text-[13px] font-medium text-white/70 md:text-sm">
                      <span className="truncate">{show.subtitle}</span>
                      {show.explicit && (
                        <Explicit className="inline-block shrink-0 fill-white/70 pt-[1px] align-baseline" />
                      )}
                    </p>
                  </div>
                </ButtonOrLink>
              </li>
            ))}
          </ul>
        </section>
      )}

      {subscriptionData?.subscription && (
        <section className="mt-11">
          <h2 className="flex items-center gap-4 text-sm font-semibold text-white lg:text-base">
            <span className="capitalize">{t("subscription")}</span>
            <span className="h-px w-full flex-1 bg-white/[.08]"></span>
          </h2>
          <SubscriptionDetailTable subscription={subscriptionData?.subscription} />
        </section>
      )}
      <div className="mt-11">
        <p className="text-center text-xs text-white/70">
          <Lock title="secure payments" className="mr-2 inline-block fill-white/70" />
          {t("secure_payments")}
          <Stripe title="stripe" className="ml-[6px] inline-block" />
        </p>
        <div className="mt-10 flex flex-col gap-4">
          <Button
            className="w-full lg:mx-auto lg:w-auto"
            size="small"
            importance="secondary"
            href={subscriptionData?.subscription?.manageUrl}
          >
            {t("manage_subscription")}
          </Button>
          {subscriptionData?.subscription?.spotifyConnected && !isSpotifyUnlinked && (
            <Button
              className="flex w-full cursor-pointer items-center gap-2.5 lg:mx-auto lg:w-auto"
              size="small"
              importance="clear"
              onClick={unlinkSpotify}
            >
              {isUnlinkingSpotify ? (
                <ArrowPathIcon className="inline-block h-4 w-4 animate-spin fill-white" />
              ) : (
                <LinkSlash className="inline-block h-4 w-4 fill-white" />
              )}
              Unlink Spotify account
            </Button>
          )}
        </div>
      </div>
    </>
  );
}
